import React, { useState, useEffect } from "react";
import { Box, Typography, Card } from "@mui/material";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import TextAreaComponent from "../../atom/Inputs/TextArea";

const MAX_LOG_LENGTH = 5000;

const QuizMistakeLogCard = ({
  attemptNumber,
  completedAt,
  initialLog = "",
  onSaveLog,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [logText, setLogText] = useState(initialLog);
  const [savedLog, setSavedLog] = useState(initialLog);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    setLogText((initialLog || "").slice(0, MAX_LOG_LENGTH));
    setSavedLog(initialLog || "");
  }, [initialLog]);

  const handleTextChange = (e) => {
    setLogText(e.target.value.slice(0, MAX_LOG_LENGTH));
    setSaveError("");
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    setSaveError("");
    try {
      const response = await onSaveLog?.(logText);
      if (!response || response?.status === false || response.status_code !== 1 ) {
        const titleError = response?.errors?.title;
        const validationMessage = Array.isArray(titleError)
          ? titleError[0]
          : titleError;
        setSaveError(
          validationMessage || response?.message || "Unable to save the mistake log."
        );
        return;
      }
      setSavedLog(logText);
      setIsEditing(false);
    } catch (error) {
      setSaveError(error?.message || "Unable to save the mistake log.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setLogText(savedLog);
    setIsEditing(false);
  };

  return (
    <Box sx={{ mt: 3, width: "100%", maxWidth: 935 }}>
      <Card
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid #E2E8F0",
          boxShadow: "none",
          backgroundColor: "#f4edf6",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 700,
            fontSize: "16px",
            color: "#0F172A",
          }}
        >
          Attempt {attemptNumber}
        </Typography>

        {completedAt && (
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 400,
              fontSize: "12px",
              color: "#64748B",
              mt: 0.5,
              mb: 2,
            }}
          >
            Completed on: {completedAt}
          </Typography>
        )}

        {!isEditing && !savedLog ? (
          <Box
            sx={{
              py: 1.5,
              px: 2,
              backgroundColor: "#F8FAFC",
              borderRadius: 2,
              textAlign: "center",
              mb: 1.5,
              border: "1px solid #F1F5F9",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Montserrat",
                color: "#94A3B8",
                fontSize: "14px",
              }}
            >
              No mistake log added for this attempt.
            </Typography>
          </Box>
        ) : null}

        {!isEditing && savedLog ? (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setIsEditing(true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setIsEditing(true);
              }
            }}
            sx={{
              p: 2,
              backgroundColor: "#F8FAFC",
              borderRadius: 2,
              mb: 3,
              border: "1px solid #E2E8F0",
              cursor: "text",
              overflow: "hidden",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                color: "#334155",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {savedLog}
            </Typography>
          </Box>
        ) : null}

        {isEditing && (
          <Box sx={{ mb: 2 }}>
            <TextAreaComponent
              height="100px"
              autoGrow
              autoFocus
              focusAtEnd
              value={logText}
              maxLength={MAX_LOG_LENGTH}
              placeholder="Write your mistake log here..."
              onchange={handleTextChange}
              style={{ backgroundColor: "#fff" }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mt: 1,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#94A3B8",
                }}
              >
                {logText.length} characters
              </Typography>
            </Box>
            {saveError && (
              <Typography color="error" sx={{ fontSize: 12, mt: 0.5 }}>
                {saveError}
              </Typography>
            )}
          </Box>
        )}

        {isEditing ? (
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
            <TextButtonComponet
              text="Cancel"
              classStyle="btn btn-secondary"
              style={{ width: 200 }}
              onButtonClick={handleCancel}
            />
            <TextButtonComponet
              text="Save"
              onButtonClick={handleSave}
              loading={isSubmitting}
              style={{ width: 200 }}
            />
          </Box>
        ) : !savedLog ? (
          <Box>
            <TextButtonComponet
              text="+ Add Mistake Log"
              onButtonClick={() => setIsEditing(true)}
              style={{ width: 200 }}
            />
          </Box>
        ) : null}
      </Card>
    </Box>
  );
};

export default QuizMistakeLogCard;